/**
 * SSR-safe date formatting utilities.
 *
 * Using toLocaleDateString() without an explicit locale causes hydration
 * mismatches because the server (Node.js) and client (browser) may have
 * different default locales.
 *
 * These helpers always use 'en-US' locale for consistent server/client output.
 */

/**
 * Format a date as "Jan 15, 2025"
 */
export function formatDate(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '-';

    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

/**
 * Format a date as "1/15/2025" (short numeric)
 */
export function formatDateShort(
    date: Date | string | null | undefined
): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '-';

    return d.toLocaleDateString('en-US');
}

/**
 * Format a date as "January 15, 2025" (long month)
 */
export function formatDateLong(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '-';

    return d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

/**
 * Format date and time as "Jan 15, 2025, 3:45 PM"
 */
export function formatDateTime(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '-';

    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

/**
 * Format time only as "3:45 PM"
 */
export function formatTime(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '-';

    return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

/**
 * Format date for jobs/tasks display: "Jan 15, 2025 at 3:45 PM"
 */
export function formatJobDate(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '-';

    const dateStr = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    const timeStr = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${dateStr} at ${timeStr}`;
}

/**
 * Format a number with locale-aware thousands separators.
 * Uses 'en-US' for consistency (commas as thousands separators).
 */
export function formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('en-US');
}

/**
 * Format currency with locale-aware formatting.
 */
export function formatCurrency(
    value: number | null | undefined,
    currency: string = 'USD'
): string {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('en-US', {
        style: 'currency',
        currency,
    });
}
